import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import ProjectForm from '../components/ProjectForm';

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/projects');
      setProjects(response.data || []);
    } catch (err) {
      setError('Failed to load projects');
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddProject = () => {
    setEditingProject(null);
    setShowModal(true);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setShowModal(true);
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      await apiClient.delete(`/projects/${id}`);
      setProjects(projects.filter(p => p.id !== id));
      showSuccess('Project deleted successfully');
    } catch (err) {
      setError('Failed to delete project');
    }
  };

  const handleSaveProject = async (projectData) => {
    try {
      if (editingProject) {
        // Update existing project
        const response = await apiClient.put(`/projects/${editingProject.id}`, projectData);
        setProjects(projects.map(p => p.id === editingProject.id ? response.data : p));
        showSuccess('Project updated successfully');
      } else {
        // Create new project
        const response = await apiClient.post(`/projects`, projectData);
        setProjects([...projects, response.data]);
        showSuccess('Project created successfully');
      }
      setShowModal(false);
    } catch (err) {
      setError('Failed to save project');
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  return (
    <div>
      <div className="content-header">
        <h2>Projects Management</h2>
        <button className="add-btn" onClick={handleAddProject}>
          + Add New Project
        </button>
      </div>

      {successMessage && <div className="alert alert-success">{successMessage}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {isLoading ? (
        <div className="table-container">
          <div className="no-data">Loading projects...</div>
        </div>
      ) : projects.length === 0 ? (
        <div className="table-container">
          <div className="no-data">No projects yet. Create your first project!</div>
        </div>
      ) : (
        <div className="table-container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
            {projects.map(project => {
              const images = Array.isArray(project.images) ? project.images : (typeof project.images === 'string' ? JSON.parse(project.images || '[]') : []);
              const techStack = Array.isArray(project.techStack) ? project.techStack : (typeof project.techStack === 'string' ? JSON.parse(project.techStack || '[]') : []);
              const features = Array.isArray(project.features) ? project.features : (typeof project.features === 'string' ? JSON.parse(project.features || '[]') : []);
              
              return (
                <div key={project.id} style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', transition: 'transform 0.2s', cursor: 'pointer' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                  {/* Image Carousel */}
                  <div style={{ position: 'relative', width: '100%', height: '200px', backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {images.length > 0 ? (
                      <img src={images[0]} alt={project.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ccc" width="100" height="100"/%3E%3C/svg%3E'} />
                    ) : (
                      <div style={{ fontSize: '12px', color: '#999' }}>No Images</div>
                    )}
                    {images.length > 0 && (
                      <div style={{ position: 'absolute', top: '10px', right: '10px', backgroundColor: 'rgba(0,0,0,0.7)', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>
                        {images.length} images
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div style={{ padding: '15px' }}>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 'bold', color: '#333' }}>{project.title}</h3>
                    <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#666', lineHeight: '1.4' }}>{project.shortDescription}</p>

                    {/* Tech Stack */}
                    {techStack.length > 0 && (
                      <div style={{ marginBottom: '10px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {techStack.slice(0, 3).map((tech, idx) => (
                            <span key={idx} style={{ display: 'inline-block', padding: '4px 8px', backgroundColor: '#e3f2fd', color: '#1976d2', borderRadius: '12px', fontSize: '11px' }}>
                              {tech}
                            </span>
                          ))}
                          {techStack.length > 3 && (
                            <span style={{ display: 'inline-block', padding: '4px 8px', backgroundColor: '#f5f5f5', color: '#666', borderRadius: '12px', fontSize: '11px' }}>
                              +{techStack.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Features */}
                    {features.length > 0 && (
                      <div style={{ marginBottom: '10px', fontSize: '12px', color: '#666' }}>
                        <strong>Features:</strong> {features.slice(0, 2).map(f => f).join(', ')}{features.length > 2 ? `...` : ''}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="action-buttons" style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                      <button
                        className="edit-btn"
                        onClick={() => handleEditProject(project)}
                        style={{ flex: 1, padding: '8px', fontSize: '12px' }}
                      >
                        Edit
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteProject(project.id)}
                        style={{ flex: 1, padding: '8px', fontSize: '12px' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <ProjectForm
              project={editingProject}
              onSave={handleSaveProject}
              onCancel={() => setShowModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
